/**
 * PeriodSelector Component
 *
 * Allows users to select different period types for cost/analytics views:
 * - Month: Current or navigable month (with arrow buttons)
 * - Quarter: Current or navigable quarter (with arrow buttons)
 * - YTD: Year to date (no navigation)
 * - Trailing 12: Last 12 months (no navigation)
 *
 * Part of US-P7-001 Step 5: Cost Summary Dashboard
 */

import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { createDate } from '@/utils/dateUtils'

/**
 * Period type constants
 */
export const PERIOD_TYPES = {
  MONTH: 'month',
  QUARTER: 'quarter',
  YTD: 'ytd',
  TRAILING_12: 'trailing12'
}

/**
 * Calculate period dates based on period type and offset
 *
 * @param {string} periodType - One of PERIOD_TYPES
 * @param {number} offset - Months/quarters offset from current (negative = past)
 * @returns {{ start: string, end: string, label: string }} Period info
 */
export function calculatePeriod(periodType, offset = 0) {
  const now = createDate()
  const year = now.getFullYear()
  const month = now.getMonth()
  const day = now.getDate()

  let start, end, label

  switch (periodType) {
    case PERIOD_TYPES.MONTH: {
      const target = createDate(year, month + offset, 1)
      const targetYear = target.getFullYear()
      const targetMonth = target.getMonth()

      start = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-01`

      // If current month (offset 0), use today as end; otherwise last day of month
      if (offset === 0) {
        end = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      } else {
        const lastDay = createDate(targetYear, targetMonth + 1, 0)
        end = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
      }

      label = target.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      break
    }

    case PERIOD_TYPES.QUARTER: {
      const currentQuarterStart = Math.floor(month / 3) * 3
      const targetQuarterStart = currentQuarterStart + (offset * 3)
      const target = createDate(year, targetQuarterStart, 1)
      const targetYear = target.getFullYear()
      const targetQMonth = target.getMonth()
      const quarterNum = Math.floor(targetQMonth / 3) + 1

      start = `${targetYear}-${String(targetQMonth + 1).padStart(2, '0')}-01`

      // If current quarter (offset 0), use today as end; otherwise last day of quarter
      if (offset === 0) {
        end = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      } else {
        const lastDay = createDate(targetYear, targetQMonth + 3, 0)
        end = `${targetYear}-${String(targetQMonth + 3).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
      }

      label = `Q${quarterNum} ${targetYear}`
      break
    }

    case PERIOD_TYPES.YTD: {
      start = `${year}-01-01`
      end = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      label = `YTD ${year}`
      break
    }

    case PERIOD_TYPES.TRAILING_12: {
      const startDate = createDate(year, month - 11, 1)
      const startYear = startDate.getFullYear()
      const startMonth = startDate.getMonth()
      start = `${startYear}-${String(startMonth + 1).padStart(2, '0')}-01`
      end = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      label = 'Trailing 12 months'
      break
    }

    default:
      throw new Error(`Invalid period type: ${periodType}`)
  }

  return { start, end, label }
}

/**
 * PeriodSelector Component
 *
 * @param {Object} props
 * @param {string} props.value - Current period type
 * @param {Function} props.onChange - Callback: (periodType, periodData) => void
 * @param {string} [props.className] - Additional CSS classes
 */
export default function PeriodSelector({ value, onChange, className = '' }) {
  const [selectedPeriod, setSelectedPeriod] = useState(value || PERIOD_TYPES.MONTH)
  const [offset, setOffset] = useState(0)

  const isNavigable = selectedPeriod === PERIOD_TYPES.MONTH || selectedPeriod === PERIOD_TYPES.QUARTER
  const canGoForward = offset < 0

  const handleChange = (newPeriod) => {
    setSelectedPeriod(newPeriod)
    setOffset(0)
    const periodData = calculatePeriod(newPeriod, 0)
    onChange?.(newPeriod, periodData)
  }

  const handleNavigate = (direction) => {
    const newOffset = offset + direction
    if (newOffset > 0) return // Don't go into the future
    setOffset(newOffset)
    const periodData = calculatePeriod(selectedPeriod, newOffset)
    onChange?.(selectedPeriod, periodData)
  }

  const buttonClass = (period) =>
    `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
      selectedPeriod === period
        ? 'bg-neutral-900 text-white'
        : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
    }`

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Navigation arrows — only for Month/Quarter */}
      {isNavigable && (
        <button
          onClick={() => handleNavigate(-1)}
          className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors"
          aria-label="Previous period"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      <button onClick={() => handleChange(PERIOD_TYPES.MONTH)} className={buttonClass(PERIOD_TYPES.MONTH)}>
        Month
      </button>
      <button onClick={() => handleChange(PERIOD_TYPES.QUARTER)} className={buttonClass(PERIOD_TYPES.QUARTER)}>
        Quarter
      </button>
      <button onClick={() => handleChange(PERIOD_TYPES.YTD)} className={buttonClass(PERIOD_TYPES.YTD)}>
        YTD
      </button>
      <button onClick={() => handleChange(PERIOD_TYPES.TRAILING_12)} className={buttonClass(PERIOD_TYPES.TRAILING_12)}>
        Trailing 12
      </button>

      {/* Forward arrow */}
      {isNavigable && (
        <button
          onClick={() => handleNavigate(1)}
          disabled={!canGoForward}
          className={`p-2 rounded-lg transition-colors ${
            canGoForward
              ? 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
              : 'text-neutral-300 cursor-not-allowed'
          }`}
          aria-label="Next period"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
